import { useState, useEffect, useRef, useMemo } from 'react';

export default function TagEditor({
  tags = [],
  onUpdate,
  suggestions = [],
  placeholder = "タグを追加...",
  showToast,
}) {
  const [inputValue, setInputValue] = useState('');
  const [localTags, setLocalTags] = useState(tags);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);
  const isInternalChange = useRef(false);

  // Sync localTags with tags prop from parent
  useEffect(() => {
    if (!isInternalChange.current) {
      setLocalTags(tags);
    }
    isInternalChange.current = false;
  }, [tags]);

  const handleUpdate = async (newTags) => {
    const previousTags = [...localTags];
    setLocalTags(newTags);
    isInternalChange.current = true;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        await onUpdate(newTags);
      } catch (error) {
        console.error('Failed to update tags:', error);
        setLocalTags(previousTags);
        isInternalChange.current = false;
        if (showToast) {
          showToast('タグの保存に失敗しました。', 'error');
        } else {
          alert('タグの保存に失敗しました。');
        }
      }
    }, 500);
  };

  const addTag = (tagName) => {
    const trimmed = (tagName || inputValue).trim().replace(/#/g, '');
    if (trimmed.length > 50) {
      if (showToast) showToast('タグ名は50文字以内で入力してください。', 'error');
      return;
    }
    if (trimmed && !localTags.includes(trimmed)) {
      handleUpdate([...localTags, trimmed]);
    }
    setInputValue('');
    setShowSuggestions(false);
    setIsInputVisible(false);
  };

  const removeTag = (tagToRemove) => {
    handleUpdate(localTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Escape') {
      setIsInputVisible(false);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const filteredSuggestions = useMemo(() => {
    const input = inputValue.toLowerCase();
    return suggestions
      .filter(s => s.toLowerCase().includes(input) && !localTags.includes(s))
      .slice(0, 5);
  }, [suggestions, inputValue, localTags]);

  useEffect(() => {
    if (isInputVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isInputVisible]);

  return (
    <div className="flex flex-wrap gap-1.5 min-h-[32px] items-center">
      {localTags.map((tag) => (
        <span
          key={tag}
          className="flex items-center gap-1 text-[10px] text-emerald-200 bg-emerald-950/60 border border-emerald-800 rounded-md px-2 py-0.5 font-bold shadow-sm group"
        >
          #{tag}
          <button
            type="button"
            onClick={() => removeTag(tag)}
            className="text-emerald-500 hover:text-white transition-colors"
            aria-label={`タグ ${tag} を削除`}
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </span>
      ))}

      {isInputVisible ? (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // Delay blur to allow suggestion click
              setTimeout(() => {
                if (!inputValue) setIsInputVisible(false);
                setShowSuggestions(false);
              }, 200);
            }}
            placeholder={placeholder}
            className="bg-gray-800 border border-emerald-500/50 rounded-md px-2 py-0.5 text-[10px] text-white outline-none w-32 font-bold"
          />
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="absolute left-0 top-full mt-1 w-40 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-[100] overflow-hidden">
              {filteredSuggestions.map(s => (
                <button
                  key={s}
                  onClick={() => addTag(s)}
                  className="w-full text-left px-3 py-1.5 text-[10px] text-gray-300 hover:bg-emerald-900/40 hover:text-emerald-200 font-bold transition-colors border-b border-gray-800 last:border-none"
                >
                  #{s}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setIsInputVisible(true)}
          className="flex items-center gap-1 text-[10px] text-gray-500 bg-gray-800/40 border border-gray-700/50 rounded-md px-2 py-0.5 font-bold hover:bg-gray-800 hover:text-gray-300 transition-all border-dashed"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
          </svg>
          タグを追加
        </button>
      )}
    </div>
  );
}
